import React, { useState, useEffect } from 'react';
import {
    IconButton,
    Menu,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Typography,
    Box,
    Divider,
    Button
} from '@mui/material';
import { ViewColumn } from '@mui/icons-material';
import { showErrorToast } from 'src/utils/Toast/SnackbarProvider';
import * as constants from 'src/utils/constants';
import { getStageDisplayName } from 'src/utils/stageDisplay';
import axios from 'axios';

const ColumnVisibilityControl = ({ columns, visibleColumns, onVisibilityChange, viewType }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [loading, setLoading] = useState(false);


    // Load user preferences on mount
    useEffect(() => {
        loadUserPreferences();
    }, [viewType]);

    const loadUserPreferences = async () => {
        setLoading(true);
        let preferencesLoaded = false;
        
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(
                `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/user-preferences/columnVisibility_${viewType}/`,
                {
                    headers: {
                        'Authorization': `Token ${token}`
                    }
                }
            );
            
            if (response.data.success && response.data.data) {
                const visibleCols = response.data.data.preference_value;
                onVisibilityChange(visibleCols);
                preferencesLoaded = true;
            }
        } catch (error) {
            console.error('API call failed:', error.response?.status, error.response?.data, error.message);
            console.log('Trying localStorage fallback');
        }
        
        // Fallback to localStorage if API didn't return data
        if (!preferencesLoaded) {
            try {
                const stored = localStorage.getItem(`columnVisibility_${viewType}`);
                if (stored) {
                    const visibleCols = JSON.parse(stored);
                    onVisibilityChange(visibleCols);
                    preferencesLoaded = true;
                }
            } catch (localError) {
                console.error('Failed to load from localStorage:', localError);
            }
        }
        
        // If no preferences found anywhere, set default to show all columns except OUTREACH and Closed stages
        if (!preferencesLoaded && columns.length > 0) {
            const filteredColumns = columns
                .filter(column => {
                    if (!column.name || column.name === 'Unknown') return false;
                    // Hide OUTREACH and Future outreach by default
                    if (column.name.toLowerCase().includes('outreach - 0%')) return false;
                    // Hide Future outreach by default
                    if (column.name.toLowerCase().includes('future outreach')) return false;
                    // Hide Closed - 100% by default
                    if (column.name.toLowerCase().includes('closed - 100%')) return false;
                    return true;
                })
                .map(col => col.name);
            onVisibilityChange(filteredColumns);
            // Save the default to backend for future use
            saveUserPreferences(filteredColumns);
        }
        
        setLoading(false);
    };

    const saveUserPreferences = async (newVisibleColumns) => {
        try {
            const token = localStorage.getItem('authToken');
            await axios.post(
                `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/user-preferences/`,
                {
                    preference_key: `columnVisibility_${viewType}`,
                    preference_value: newVisibleColumns
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    }
                }
            );
            
            // Also save to localStorage as backup
            localStorage.setItem(
                `columnVisibility_${viewType}`, 
                JSON.stringify(newVisibleColumns)
            );
        } catch (error) {
            console.error('Failed to save preferences to backend:', error);
            
            // Fallback to localStorage only
            try {
                localStorage.setItem(
                    `columnVisibility_${viewType}`, 
                    JSON.stringify(newVisibleColumns)
                );
            } catch (localError) {
                console.error('Failed to save preferences to localStorage:', localError);
                showErrorToast('Failed to save column preferences');
            }
        }
    };

    const handleToggleColumn = (columnName) => {
        const newVisibleColumns = visibleColumns.includes(columnName)
            ? visibleColumns.filter(col => col !== columnName)
            : [...visibleColumns, columnName];
        
        onVisibilityChange(newVisibleColumns);
        saveUserPreferences(newVisibleColumns);
    };

    const handleShowAll = () => {
        const allColumns = columns.map(col => col.name);
        onVisibilityChange(allColumns);
        saveUserPreferences(allColumns);
    };

    const handleHideAll = () => {
        onVisibilityChange([]);
        saveUserPreferences([]);
    };

    return (
        <>
            <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                size="small"
                disabled={loading}
                sx={{ ml: 1 }}
            >
                <ViewColumn />
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                PaperProps={{
                    sx: { minWidth: 200, maxHeight: 400 }
                }}
            >
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Column Visibility
                    </Typography>
                </Box>
                
                <Divider />
                
                <Box sx={{ px: 1, py: 0.5 }}>
                    <Button
                        size="small"
                        onClick={handleShowAll}
                        sx={{ mr: 1, fontSize: '0.75rem' }}
                    >
                        Show All
                    </Button>
                    <Button
                        size="small"
                        onClick={handleHideAll}
                        sx={{ fontSize: '0.75rem' }}
                    >
                        Hide All
                    </Button>
                </Box>
                
                <Divider />
                
                {columns
                    .filter(column => {
                        if (!column.name || column.name === 'Unknown') return false;
                        return true;
                    })
                    .map((column) => (
                    <MenuItem
                        key={column.name}
                        onClick={() => handleToggleColumn(column.name)}
                        dense
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={visibleColumns.includes(column.name)}
                                    size="small"
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    {getStageDisplayName(column.name)} ({column.count})
                                </Typography>
                            }
                            sx={{ m: 0, width: '100%' }}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default ColumnVisibilityControl;